import {
  ArrowForward,
  CheckCircleOutlineOutlined,
  DescriptionOutlined,
  ErrorOutlineOutlined,
  TimerOffOutlined,
  TimerOutlined,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import * as dayjs from "dayjs";
import api from "../../../services/api";
import { ptBR } from "@mui/x-data-grid/locales";
import { ReactComponent as EmptyBox } from "../../../assets/easyReport/iconSvg/emptyBox.svg";
import { AgendamentoRelatorio } from "../../../pages/easyReport/agendamento/agendamenoDetalhado";
import { useNotification } from "../../notificationProvider";

const stylesPage = (theme: Theme) => ({
  paper: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    width: "100%",
    borderRadius: 2,
    overflow: "hidden",
  },
  dataGrid: {
    width: "100%",
    height: "100%",
    minHeight: 500,
    fontSize: 12,
    borderRadius: 0,
    border: 0,
    backgroundColor: "transparent",
    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
      outline: "none !important",
    },
    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main} !important`,
      outlineOffset: "-2px",
    },
    ".MuiDataGrid-cell": {
      display: "flex",
      alignItems: "center",
      borderColor: theme.palette.stroke,
    },
    "& .MuiDataGrid-scrollbar--vertical": {
      display: "none",
    },
    "& .MuiDataGrid-filler": {
      display: "none",
    },
    "& .MuiDataGrid-scrollbarFiller--borderTop": {
      display: "none",
    },
    "& .MuiDataGrid-scrollbarFiller--header": {
      backgroundColor: theme.palette.background.paper,
      borderTop: `1px solid ${theme.palette.stroke}`,
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      color: "text.primary", // Altera a cor do titulo do cabeçalho
    },
    "& .MuiDataGrid-footerContainer": {
      backgroundColor: "transparent", // Altera cor do rodapé
      border: 0,
      borderTop: `1px solid ${theme.palette.stroke}`,
      height: "45px",
    },
    "& .MuiDataGrid-columnHeader": {
      backgroundColor: theme.palette.background.paper, // Altera cor do cabeçalho
      border: 0,
      borderTop: `1px solid ${theme.palette.stroke}`,
      width: "100%",
    },
    "& .MuiDataGrid-topContainer": {
      border: 0,
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiDataGrid-row--borderBottom": {
      border: 0,
    },
    "& .MuiDataGrid-columnHeaders": {
      border: 0,
      height: "55px",
      backgroundColor: theme.palette.background.paper,
    },

    "& .even-row": { backgroundColor: theme.palette.table },
    "& .odd-row": { backgroundColor: theme.palette.background.paper },
  },
  boxRenderCell: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    height: "100%",
    alignItems: "center",
    gap: 1,
  },
  typography: {
    fontSize: "0.8rem",
  },
  chip: {
    borderRadius: "8px",
    fontWeight: 500,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.cardProfile,
    padding: "0px 8px",
    height: "28px",
    maxWidth: "100px",
    marginRight: "4px",
    "& .MuiChip-label": {
      padding: 0,
    },
  },
  boxNoRowsOverlay: {
    p: 2,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  iconUserNotFound: {
    width: "140px",
    height: "140px",
    fill: theme.palette.text.disabled,
  },
  containerColumnLastLogin: {
    display: "flex",
    justifyContent: "start",
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 0.5,
    height: "100%",
  },
  textDate: {
    color: "text.primary",
    lineHeight: 1.5,
  },
  boxRenderCellIcon: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
  },
});
interface AgendamentoRelatoriosProps {
  id?: string | null;
  searchValue?: string;
  filtroDataInicial: any;
  filtroDataFinal: any;
  filtroGeradoEm?: any;
}

export default function AgendamentoRelatorios({
  id = null,
  searchValue = "",
  filtroDataFinal,
  filtroDataInicial,
  filtroGeradoEm,
}: AgendamentoRelatoriosProps) {
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AgendamentoRelatorio[]>([]);
  const theme = useTheme();
  const styles = stylesPage(theme);
  const notify = useNotification();

  useEffect(() => {
    const fetchAppointmentReports = async () => {
      try {
        setLoading(true);

        const res = await api.post("/easyReport/v2/findManySchedulesReport");

        const filteredRelatorios = res.data.filter(
          (relatorio: AgendamentoRelatorio) => relatorio.status === 1,
        );
        setRows(filteredRelatorios);
      } catch (error) {
        console.error("Erro ao buscar relatórios:", error);
        notify("Erro ao carregar histórico.", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchAppointmentReports();
  }, []);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (id && row?.agendamentoId !== id) {
        return false;
      }

      if (searchValue) {
        const search = searchValue.toLowerCase();
        const matchesSearch =
          row?.titulo?.toLowerCase().includes(search) ||
          row?.items?.some((item) => item.toLowerCase().includes(search)) ||
          row?.hosts?.some((item) => item.toLowerCase().includes(search)) ||
          row?.profiles?.some((item) => item.toLowerCase().includes(search));

        if (!matchesSearch) return false;
      }

      const dataGeracao = row?.dataGeracao ? dayjs(row.dataGeracao) : null;

      if (filtroDataInicial && dataGeracao) {
        if (!dataGeracao.isSameOrAfter(dayjs(filtroDataInicial), "day")) {
          return false;
        }
      }

      if (filtroDataFinal && dataGeracao) {
        if (!dataGeracao.isSameOrBefore(dayjs(filtroDataFinal), "day")) {
          return false;
        }
      }

      if (filtroGeradoEm && dataGeracao) {
        if (!dataGeracao.isSame(dayjs(filtroGeradoEm), "day")) {
          return false;
        }
      }

      return true;
    });
  }, [
    id,
    rows,
    searchValue,
    filtroDataFinal,
    filtroDataInicial,
    filtroGeradoEm,
  ]);

  const columns: GridColDef<AgendamentoRelatorio>[] = [
    {
      field: "titulo",
      headerName: "Agendamento",
      sortable: false,
      width: 120,
    },
    {
      field: "intervaloColeta",
      headerName: "Intervalo de Coleta",
      sortable: false,
      width: 240,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => (
        <Box sx={styles.boxRenderCell}>
          <Typography sx={styles.typography}>
            {dayjs(params.row.dataInicialColeta).format("DD/MM/YYYY")}
          </Typography>
          <ArrowForward
            sx={{ fontSize: "1.1rem", color: theme.palette.text.primary }}
          />
          <Typography sx={styles.typography}>
            {dayjs(params.row.dataGeracao).format("DD/MM/YYYY")}
          </Typography>
        </Box>
      ),
    },
    {
      field: "dataGeracao",
      headerName: "Gerado em",
      width: 130,
      renderCell: (
        params: GridRenderCellParams<AgendamentoRelatorio, string>,
      ) =>
        params.value && (
          <Stack sx={styles.containerColumnLastLogin}>
            <Typography fontSize={13} sx={styles.textDate}>
              {dayjs(params.value).format("DD/MM/YYYY")}
            </Typography>
          </Stack>
        ),
    },
    {
      field: "profiles",
      headerName: "Perfil de Relatório",
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => (
        <Box sx={styles.boxRenderCell}>
          {Array.isArray(params.value) &&
            params.value.map((perfil) => (
              <Tooltip title={perfil}>
                <Chip
                  key={perfil}
                  label={perfil}
                  sx={{
                    ...styles.chip,
                    maxWidth: "100px",
                  }}
                />
              </Tooltip>
            ))}
        </Box>
      ),
    },
    {
      field: "hosts",
      headerName: "Hosts",
      minWidth: 200,
      flex: 2,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => {
        const value = params.value || [];
        const display = value.join(", ");
        return (
          <Tooltip title={display}>
            <Box
              sx={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                width: "100%",
              }}
            >
              {display}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "items",
      headerName: "Itens",
      minWidth: 300,
      flex: 3,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => {
        return (
          <Box sx={styles.boxRenderCell}>
            {params.value?.map((item: string) => (
              <Tooltip title={item}>
                <Chip sx={styles.chip} label={item} key={item} />
              </Tooltip>
            ))}
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 60,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => (
        <Box sx={styles.boxRenderCellIcon}>
          {params.row.status === 1 ? (
            <Tooltip title="Relatório Gerado">
              <Box
                component="span"
                tabIndex={0}
                aria-label="Relatório Gerado"
                sx={{ display: "inline-flex" }}
              >
                <CheckCircleOutlineOutlined color="success" />
              </Box>
            </Tooltip>
          ) : params.row.status === 2 ? (
            <Tooltip title="Erro ao gerar relatório">
              <Box
                component="span"
                tabIndex={0}
                aria-label="Erro ao gerar relatório"
                sx={{ display: "inline-flex" }}
              >
                <ErrorOutlineOutlined color="error" />
              </Box>
            </Tooltip>
          ) : params.row.status === 0 ? (
            <Tooltip title="Relatório Não gerado">
              <Box
                component="span"
                tabIndex={0}
                aria-label="Relatório Não gerado"
                sx={{ display: "inline-flex" }}
              >
                <TimerOutlined />
              </Box>
            </Tooltip>
          ) : (
            <Tooltip title="Relatório ultrapassou data">
              <Box
                component="span"
                tabIndex={0}
                aria-label="Relatório ultrapassou data"
                sx={{ display: "inline-flex" }}
              >
                <TimerOffOutlined sx={{ color: theme.palette.text.disabled }} />
              </Box>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "etapa",
      headerName: "Etapa",
      width: 60,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => (
        <Box sx={styles.boxRenderCellIcon}>
          {`${params.row.etapa}/${params.row.ocorrencias}`}
        </Box>
      ),
    },
    {
      field: "visualizar",
      headerName: "Ações",
      headerAlign: "center",
      width: 85,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => {
        const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          try {
            const response = await api.post(
              "/easyReport/v2/downloadReport",
              { id: params.row.relatorioId },
              { responseType: "blob" },
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              `${params.row.titulo || "relatorio"}.pdf`,
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          } catch (error) {
            console.error("Erro ao baixar relatório:", error);
            notify("Erro ao baixar relatório.", "error");
          }
        };
        return (
          <Tooltip title="Visualizar">
            <Box sx={styles.boxRenderCellIcon}>
              <IconButton
                disabled={params.row.status !== 1}
                onClick={handleClick}
              >
                <DescriptionOutlined
                  color={params.row.status !== 1 ? "disabled" : "primary"}
                />
              </IconButton>
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  function CustomNoRowsOverlay() {
    return (
      <Box sx={styles.boxNoRowsOverlay}>
        <EmptyBox style={styles.iconUserNotFound} />
        <Typography color="text.disabled">
          Nenhum relatório gerado encontrado para este agendamento
        </Typography>
      </Box>
    );
  }

  return (
    <Paper sx={styles.paper}>
      <DataGrid
        sx={styles.dataGrid}
        rows={filteredRows}
        columns={columns}
        loading={loading}
        disableColumnMenu={true}
        pageSizeOptions={[10, 25, 50, 100]}
        onPaginationModelChange={(model) => setPageSize(model.pageSize)}
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
        }
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        initialState={{
          pagination: {
            paginationModel: { pageSize: pageSize },
          },
        }}
      />
    </Paper>
  );
}
